import _ from 'underscore';
import { ConfiguredValidators } from './property-validators/index';
import { imageAssetSerializeSchema, imageAssetDeserializeSchema } from './image-metadata-schema';

const validateProperty = ({
  propertyValue,
  propertySchema,
  effectKey,
}) => {
  let result = true;
  let ignoreInvalidProperty = false;

  if (_.isUndefined(propertyValue)) {
    result = !!propertySchema.nullable;
  } else {
    for (let i = 0; i < ConfiguredValidators.length; i += 1) {
      const ValidatorClass = ConfiguredValidators[i];
      const instance = new ValidatorClass(propertySchema);
      const childResult = instance.validate(propertyValue, effectKey);

      // once there is failure by validator that could not ignore error, break
      if (!childResult && !instance.config.ignoreInvalidProperty) {
        result = false;
        ignoreInvalidProperty = false;

        break;
      }

      // if validatior is configured to ignore error, continue rest validations regardless of result
      if (!childResult && instance.config.ignoreInvalidProperty) {
        result = false;
        ignoreInvalidProperty = true;
      }
    }
  }

  return {
    result,
    ignoreInvalidProperty,
  };
};

// Return true if all values including nested are null/undefined
// e.g. For value: { a: { b: null }}, it returns true
const shouldFilterOut = (value) => {
  if (_.isObject(value)) {
    const hasValidKey = _.some(value, val => !shouldFilterOut(val));

    return !hasValidKey;
  }

  return _.isNull(value) || _.isUndefined(value);
};

const processObjectWithSchema = ({
  obj,
  schema,
  validatePropertyFunc,
}) => {
  const { result, ignoreInvalidProperty } = validatePropertyFunc({
    propertyValue: obj,
    propertySchema: schema,
  });

  if (!result && !ignoreInvalidProperty) {
    throw new Error(`Validate property ${schema.name || schema.alias} failed`);
  }

  // if ignoreInvalidProperty is true, return an empty value so it can be filtered out.
  if (!result) {
    return null;
  }

  if (_.isObject(obj)) {
    return _.reduce(obj, (memo, val, key) => {
      const childkeySchema = schema.properties[key];
      const newKey = childkeySchema.alias;

      if (!newKey) {
        throw new Error(`Unsupported! please add metadata to schema for key: ${key}`);
      }

      const newValue = processObjectWithSchema({
        obj: val,
        schema: childkeySchema,
        validatePropertyFunc,
      });

      if (shouldFilterOut(newValue)) {
        if (!childkeySchema.nullable) {
          throw new Error(`the value for key ${key} is empty, but from schema, nullable is not allowed`);
        }

        return memo;
      }

      return {
        ...memo,
        [newKey]: newValue,
      };
    }, {});
  }

  return obj;
};

const validateDeserializeProperty = ({
  propertyValue,
  propertySchema,
}) => {
  let result = true;

  _.keys(propertyValue).forEach((key) => {
    const childkeySchema = propertySchema.properties[key];

    if (!childkeySchema) {
      result = false;
    }
  });

  return {
    result,
    ignoreInvalidProperty: true,
  };
};

export const extractInfoForSerialization = ({ obj, effectKey }) =>
  processObjectWithSchema({
    obj,
    schema: imageAssetSerializeSchema,
    validatePropertyFunc: options => validateProperty({ ...options, effectKey }),
  });

export const extractInfoForDeserialization = ({ obj }) =>
  processObjectWithSchema({
    obj,
    schema: imageAssetDeserializeSchema,
    validatePropertyFunc: validateDeserializeProperty, // no validators
  });
